// `next/link` replacement (ADR-017).
//
// React Router's Link takes `to`; Next's took `href`. Rather than touch 15 call
// sites across the app, this wrapper keeps the `href` prop name and forwards
// everything else. Same reasoning as `lib/navigation.ts`: keep the migration
// diff mechanical.
//
// External and protocol-relative hrefs fall through to a plain <a>. React
// Router would otherwise treat "https://…" as an in-app path and route to a
// 404 rather than leaving the site.

import * as React from "react";
import { Link as RouterLink } from "react-router-dom";

type AnchorProps = Omit<React.ComponentPropsWithoutRef<"a">, "href">;

export interface LinkProps extends AnchorProps {
  href: string;
  replace?: boolean;
}

function isExternal(href: string): boolean {
  return /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(href);
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, replace, children, ...rest },
  ref,
) {
  if (isExternal(href)) {
    return (
      <a ref={ref} href={href} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <RouterLink ref={ref} to={href} replace={replace} {...rest}>
      {children}
    </RouterLink>
  );
});

export default Link;
